/*
  Конвейер подачи — §3.4.6.

  Четыре сводных числа и очередь под ними. Самое важное из четырёх —
  «получено, но не предъявлено»: именно здесь, а не в суде, теряются деньги
  (§3.6), поэтому строка выделена и показывается всегда.

  Подача остаётся явным шагом человека (§3.4.4). Кнопка называется «отметить
  поданными», а не «подать»: единого программного интерфейса подачи мировым
  судьям в стране не существует, и делать вид, что он есть, нельзя.
*/

import { useCallback, useEffect, useRef, useState } from 'react'

import { api } from '../api/client.js'
import { assemblyOf, can } from '../components/access.js'
import { accounts, date, days, money, percent } from '../api/format.js'
import { Empty, Loading, MoneyCard, Notice, Tag } from '../components/Common.jsx'
import ConveyorChosen, { useConveyorChosen } from '../components/ConveyorChosen.jsx'
import { ChosenQueue, ServerQueue } from '../components/ConveyorQueueSource.jsx'
import UndocumentedOrders from '../components/UndocumentedOrders.jsx'
import useCourtSections from '../components/useCourtSections.js'
import WaveRegistries from '../components/WaveRegistries.jsx'

export default function ConveyorPage({ profile }) {
  const [board, setBoard] = useState(null)
  const [error, setError] = useState(null)
  const [notice, setNotice] = useState(null)
  const [busy, setBusy] = useState(false)
  // Версия очереди (21.10.2026): растёт, когда очередь могла измениться, и серверная
  // страница перечитывается. Саму очередь страница не держит - её листает сервер. С
  // 23.10.2026 по ней же перечитываются и реестры волн (`WaveRegistries`).
  const [queueVersion, setQueueVersion] = useState(0)
  // Участки нужны полю под очередью - тому, которым волну сводят в один реестр.
  const { sections, error: sectionsError } = useCourtSections(
    profile.permissions.includes('build_documents'),
  )
  // Счета, выбранные в реестре кнопкой «В конвейер» (16.10.2026). Пока они
  // есть, очередь показывает только их; «Показать всю очередь» их снимает.
  const { ids, chosen, error: chosenError, showAll } = useConveyorChosen()

  // Отказ действия («Собрать реестр», «Отметить поданными») - отдельно от сбоя чтения
  // (аудит 26.10.2026, причина 1): общая ошибка заменяла весь экран плашкой, и выбор
  // из десятков счетов пропадал вместе с очередью.
  const [actionError, setActionError] = useState(null)
  // Ответ на устаревшее чтение выбрасывается (причина 5).
  const latest = useRef(0)

  const reload = useCallback(() => {
    const mine = (latest.current += 1)
    setQueueVersion((value) => value + 1)
    api
      .conveyor()
      .then((result) => {
        if (mine !== latest.current) return
        setBoard(result)
        setError(null)
      })
      .catch((refusal) => {
        if (mine === latest.current) setError(refusal)
      })
  }, [])

  useEffect(reload, [reload])

  const failure = error ?? chosenError
  if (!board) return failure ? <Notice error={failure} /> : <Loading what="Собираем очередь к подаче" />
  if (ids?.length && !chosen && !failure) return <Loading what="Собираем очередь к подаче" />

  const canAct = can(profile, 'manage_stages')
  // Реестр волны - сборка документов, ярус «Под ключ» её закрывает (строка 127).
  const assemble = assemblyOf(profile)

  /*
    Возвращает признак «получилось»: очередь по нему решает, сбрасывать ли
    выбор. Сбрасывать после отказа сервера нельзя - человек потерял бы отбор из
    сорока семи строк и не понял бы, почему.
  */
  async function runAction(kind, chosen, court) {
    setBusy(true)
    setNotice(null)
    setActionError(null)
    try {
      if (kind === 'registry') {
        const built = await api.waveRegistry(chosen, court)
        // Место называется точно: раздела «документы организации» в кабинете
        // нет, и до 09.09.2026 это сообщение отправляло человека искать
        // несуществующий экран.
        //
        // Число реестров называется с 11.09.2026: волна из счетов разных
        // участков даёт по реестру на каждый, и «реестр сформирован» в
        // единственном числе скрывало бы, что в суд нести придётся несколько
        // пачек.
        const text =
          built.length > 1
            ? `Реестров волны сформировано: ${built.length}, по одному на участок - они ниже, в «Реестрах волн».`
            : 'Реестр волны сформирован - он ниже, в «Реестрах волн».'
        // Предупреждения реестров - тут же (26.10.2026, строка 133: пробная партия
        // для заявлений без имени должника). Реестр собран; это совет, а не отказ.
        const told = [...new Set(built.flatMap((doc) => doc.warnings ?? []))]
        setNotice({ kind: told.length ? 'warn' : 'ok', text, told })
      } else {
        const outcomes = await api.markFiled(chosen)
        const changed = outcomes.filter((outcome) => outcome.changed).length
        setNotice({ kind: 'ok', text: `Отмечено поданными: ${changed} из ${outcomes.length}.` })
      }
      reload()
      // Выбор из реестра отработал: очередь после действия уже другая, и
      // держать на экране прежний срез значило бы показывать поданные счета
      // как ждущие подачи.
      if (ids) showAll()
      return true
    } catch (refusal) {
      setActionError(refusal)
      return false
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <header className="head">
        <div>
          <h1 className="head__title">Конвейер подачи</h1>
          <div className="head__note">Расчёт на {date(board.as_of)}</div>
        </div>
      </header>

      <Notice error={actionError ?? failure} wide />
      {notice ? (
        <Notice kind={notice.kind}>
          <div className="notice__what">{notice.text}</div>
          {(notice.told ?? []).map((line) => (
            <div key={line} className="notice__do">
              {line}
            </div>
          ))}
        </Notice>
      ) : null}

      {/*
        Надпись о неполном разборе стоит ПЕРВОЙ и выше всех чисел: она
        относится ко всему, что ниже, - и к сводке, и к очереди. Решение
        владельца 29.09.2026: экран показывает последний посчитанный разбор, а
        не пересчитывает портфель в запросе. Цена решения в том, что очередь
        может быть неполной, и человек обязан узнать это от экрана, а не
        догадаться по короткому списку.
      */}
      {board.triage_ready < board.triage_total ? (
        <div className="notice notice--warn notice--wide">
          <div className="notice__what">
            Разбор вашего портфеля пересчитывается: готово {accounts(board.triage_ready)} из{' '}
            {board.triage_total}
          </div>
          <div className="notice__do">
            Ниже - последний посчитанный разбор, поэтому очередь может быть короче настоящей, а
            суммы занижены. Пересчёт идёт сам и начинается сразу после загрузки начислений;
            откройте страницу заново через несколько минут. На заявление и пошлину это не влияет:
            они считаются по свежим начислениям в момент сборки пакета.
          </div>
        </div>
      ) : null}

      {board.not_enforced > 0 ? (
        <div className="notice notice--warn notice--wide">
          <div className="notice__what">
            Приказов получено {board.received_total}, из них не предъявлено {board.not_enforced}{' '}
            на {money(board.not_enforced_amount)}
            {board.oldest_not_enforced_days
              ? `; самый давний лежит ${days(board.oldest_not_enforced_days)}`
              : ''}
          </div>
          <div className="notice__do">
            Именно здесь, а не в суде, теряются деньги: приказ сам по себе ничего не взыскивает.
            Предъявите его в банк должника или приставу.
          </div>
        </div>
      ) : null}

      <UndocumentedOrders
        count={board.orders_without_document}
        accounts={board.orders_without_document_accounts}
      />

      <div className="grid">
        <MoneyCard
          label="К подаче"
          value={board.ready_amount}
          note={`${accounts(board.ready_accounts)} · пошлина ${money(board.ready_state_fee)}`}
        />
        <MoneyCard
          label="Подано, ждём"
          value={board.filed_amount}
          note={
            board.filed_average_days === null
              ? `${accounts(board.filed_accounts)}`
              : `${accounts(board.filed_accounts)} · в среднем ${days(board.filed_average_days)}`
          }
        />
        <div className="card">
          <div className="money__label">Получено приказов</div>
          <div className="money__value">{board.received_total}</div>
          <div className="money__note">
            не предъявлено: {board.not_enforced}
            {board.not_enforced > 0 ? ` на ${money(board.not_enforced_amount)}` : ''}
          </div>
        </div>
        <div className="card">
          <div className="money__label">Отменено</div>
          <div className="money__value">{board.cancelled}</div>
          <div className="money__note">
            {percent(board.cancellation_share)} от вынесенных{' '}
            {board.cancellation_is_alarming ? (
              <Tag kind="warn">досудебная работа слабая</Tag>
            ) : null}
          </div>
        </div>
      </div>

      <section className="section">
        <h2 className="section__title">К подаче</h2>
        {chosen ? <ConveyorChosen chosen={chosen} total={ids.length} onShowAll={showAll} /> : null}
        {chosen ? (
          chosen.queue.length ? (
            <ChosenQueue
              key="chosen"
              queue={chosen.queue}
              sections={sections}
              sectionsError={sectionsError}
              canAct={canAct}
              assemble={assemble}
              busy={busy}
              limit={board.filing_limit}
              onAction={runAction}
            />
          ) : null
        ) : board.ready_accounts === 0 && board.triage_ready < board.triage_total ? (
          // Пусто по двум разным причинам, и человеку они говорят разное:
          // «подавать некого» - это вывод, а «ещё не посчитали» - ожидание.
          // Один текст на оба случая отправил бы владельца искать в реестре
          // помеху, которой нет (29.09.2026).
          <Empty title="Разбор ещё считается">
            Очередь появится, когда разбор дойдёт до ваших счетов: готово {accounts(board.triage_ready)}{' '}
            из {board.triage_total}. Пересчёт идёт сам - откройте страницу заново через несколько
            минут.
          </Empty>
        ) : board.ready_accounts === 0 ? (
          <Empty title="Очередь пуста">
            Сюда попадают счета, по которым триаж решил, что подача окупается. Пока таких нет:
            загрузите свежую выгрузку или проверьте в реестре, что мешает - банкротство,
            отрицательная отдача или незаполненные тарифы.
          </Empty>
        ) : (
          // Число «К подаче» берётся из сводки (`ready_accounts`), строки - страницей с
          // сервера: у панели страниц своё «Всего», по тому же отбору.
          <ServerQueue
            version={queueVersion}
            sections={sections}
            sectionsError={sectionsError}
            canAct={canAct}
            assemble={assemble}
            busy={busy}
            limit={board.filing_limit}
            onAction={runAction}
          />
        )}
      </section>

      {/* Реестры волн грузятся своими страницами: к сводке они не относятся, а живут под
          очередью - там же, где их и собирают (23.10.2026 - страницы режет сервер). */}
      <WaveRegistries
        version={queueVersion}
        onDeleted={
          assemble.allowed
            ? () => {
                setNotice({ kind: 'ok', text: 'Реестр волны удалён.' })
                reload()
              }
            : undefined
        }
      />
    </>
  )
}
